/* SignalCard — één signaal-rij met inline contacts, source-citations,
 * approve/reject/archive acties en interne Cronos-match banner.
 * Geëxtraheerd uit Signals.jsx (refactor).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import {
    Radar, Plus, Loader2, Sparkles, UserPlus, Trash2, Linkedin, Mail, Phone,
    Calendar, Users, ChevronDown, ChevronRight, AlertTriangle, Building2,
    Link2, BookOpen, Globe, Archive, ArchiveRestore, ThumbsUp, ThumbsDown, ShieldCheck, Flame,
} from "lucide-react";
import { PromoteCampaignPicker } from "./PromoteCampaignPicker";
import { fmtDateTime } from "./format";
import { TrustBadge } from "@/components/common/TrustBadge";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import ContactDrawer from "@/components/ContactDrawer";

const TYPE_COLORS = {
    funding: "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/30",
    hiring: "bg-[var(--jr-amber)]/15 text-[var(--jr-amber)] border-[var(--jr-amber)]/30",
    expansion: "bg-[var(--jr-info)]/15 text-[var(--jr-info)] border-[var(--jr-info)]/30",
    leadership_change: "bg-[var(--jr-success)]/15 text-[var(--jr-success)] border-[var(--jr-success)]/30",
    product_launch: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    vacancy_burst: "bg-purple-500/15 text-purple-300 border-purple-500/40",
    layoff: "bg-[#ef4444]/15 text-[#f87171] border-[#ef4444]/30",
    persona_match: "bg-[#6366f1]/15 text-[#818cf8] border-[#6366f1]/30",
};

// Visual left-border tint per mission_type so users instantly see whether a
// row is a growth signal (orange), vacancy burst (purple) or the new reverse-
// hunt candidate→vacancies row (blue).
const MISSION_TINT = {
    growth_signals: { color: "#FF5A00", label: "Signal Hunt" },
    vacancy_burst: { color: "#a855f7", get label() { return i18n.t("sigcard.vacancy_hunt"); } },
    candidate_to_vacancies: { color: "#3b82f6", get label() { return i18n.t("sigcard.cand_to_vac"); } },
    look_alike: { color: "#22c55e", label: "Look-alike" },
    buyer_intent: { color: "#eab308", label: "Buyer-intent" },
    competitor_churn: { color: "#ef4444", label: "Competitor-churn" },
};

const SOURCE_LABELS = {
    llm: { label: "LLM", icon: Sparkles, get hint() { return i18n.t("sigcard.hint_llm"); } },
    apollo: { label: "Apollo.io", icon: Globe },
    perplexity: { label: "Perplexity Sonar Pro", icon: Sparkles, get hint() { return i18n.t("sigcard.hint_pplx"); } },
    serper: { label: "Serper (Google)", icon: Globe, get hint() { return i18n.t("sigcard.hint_serper"); } },
    zevari: { label: "Zevari (LinkedIn)", icon: Globe, get hint() { return i18n.t("sigcard.hint_zevari"); } },
    apollo_llm_fallback: { label: "Apollo (LLM fallback)", icon: Sparkles, get hint() { return i18n.t("sigcard.hint_apollo_fb"); } },
    perplexity_llm_fallback: { label: "Perplexity (LLM fallback)", icon: Sparkles, get hint() { return i18n.t("sigcard.hint_pplx_fb"); } },
    serper_llm_fallback: { label: "Serper (LLM fallback)", icon: Sparkles, get hint() { return i18n.t("sigcard.hint_serper_fb"); } },
    demo_seed: { label: "Demo seed", icon: Globe, get hint() { return i18n.t("sigcard.hint_demo"); } },
};

/**
 * SourceChips — toont elke citation van een signaal als klikbare pill
 * (host + datum) + een expandable "Bronnen (n)" lijst met titel/datum.
 * Krijgt voorrang als source_urls aanwezig is; valt anders terug op een
 * neutrale source-pill (LLM, Apollo, Perplexity, demo seed, ...).
 */
/* ------------------------------------------------------------------ */
/*  JobLinks — directe doorklik naar de gevonden vacature(s) met het   */
/*  logo van de jobsite (LinkedIn, Indeed, StepStone, VDAB, ...)       */
/* ------------------------------------------------------------------ */
const JOB_SITE_NAMES = [
    [/linkedin\.[a-z.]+\/jobs/i, "LinkedIn Jobs"],
    [/indeed\./i, "Indeed"],
    [/stepstone\./i, "StepStone"],
    [/vdab\.be/i, "VDAB"],
    [/jobat\.be/i, "Jobat"],
    [/glassdoor\./i, "Glassdoor"],
    [/monster\./i, "Monster"],
    [/welcometothejungle\./i, "Welcome to the Jungle"],
    [/ictjob\.be/i, "ICTjob"],
];
const JOB_URL_HINT = /\/(jobs?|careers?|vacatures?|vacancies|vacature|emplois?)([/\-.]|$)/i;

function jobLinksOf(signal) {
    const seen = new Set();
    const out = [];
    const push = (url, title) => {
        if (!url) return;
        const key = url.replace(/[#?].*$/, "").replace(/\/$/, "");
        if (seen.has(key)) return;
        let host;
        try { host = new URL(url).hostname.replace(/^www\./, ""); } catch { return; }
        const named = JOB_SITE_NAMES.find(([re]) => re.test(url));
        if (!named && !JOB_URL_HINT.test(url)) return; // geen vacature-URL
        seen.add(key);
        out.push({ url, title: title || "", host, site: named ? named[1] : host });
    };
    (signal.source_urls || []).forEach((c) => push(c.url, c.title));
    push(signal.careers_page_url, i18n.t("sigcard.careers_page"));
    return out.slice(0, 4);
}

function JobLinks({ signal }) {
    const links = jobLinksOf(signal);
    if (links.length === 0) return null;
    return (
        <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid={`job-links-${signal.id}`}>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                {i18n.t("sigcard.vacancy_link_label")}
            </span>
            {links.map((l, i) => (
                <a key={i} href={l.url} target="_blank" rel="noreferrer"
                    title={l.title || l.url} data-testid={`job-link-${signal.id}-${i}`}
                    className="inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full border border-[#22c55e]/35 bg-[#22c55e]/[0.07] hover:bg-[#22c55e]/[0.16] transition-colors text-[11px] text-[#22c55e] max-w-[320px]">
                    <img src={`https://www.google.com/s2/favicons?domain=${l.host}&sz=32`}
                        alt={l.site} width={14} height={14} className="rounded-sm shrink-0"
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <span className="font-medium shrink-0">{l.site}</span>
                    {l.title && <span className="text-[var(--jr-text-3)] truncate">· {l.title}</span>}
                    <Link2 size={9} className="shrink-0 opacity-70" />
                </a>
            ))}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  ApplyCta — solliciteer-knop, ALLEEN op laag 'vacature' met werkende */
/*  Apply (apply_cta gezet); link = job_url. Nooit "waarschijnlijk open". */
/* ------------------------------------------------------------------ */
function ApplyCta({ signal }) {
    const p = signal.job_posting;
    if (signal.layer !== "vacature" || !p?.apply_cta || p.apply_cta === "none" || !p.job_url) return null;
    return (
        <div className="mt-2.5">
            <a href={p.job_url} target="_blank" rel="noreferrer" data-testid={`apply-cta-${signal.id}`}
                title={`${i18n.t("sigcard.layer_vacature_tip")} · ${String(p.last_checked_at).slice(0, 16).replace("T", " ")}`}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#22c55e] text-black text-xs font-semibold hover:bg-[#16a34a] transition-colors">
                <Link2 size={11} /> {i18n.t("sigcard.apply_cta")}
                {p.apply_cta === "easy_apply" && <span className="font-normal opacity-75">· Easy Apply</span>}
            </a>
        </div>
    );
}

function SourceChips({ signal }) {
    const [open, setOpen] = useState(false);
    const urls = signal.source_urls || [];
    const meta = SOURCE_LABELS[signal.source] || { label: signal.source || i18n.t("companies.unknown"), icon: BookOpen };
    const SrcIcon = meta.icon || BookOpen;

    const hostOf = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };

    return (
        <div className="mt-3 space-y-1.5" data-testid={`signal-sources-${signal.id}`}>
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1">
                    <SrcIcon size={10} /> {meta.label}
                </span>
                {urls.slice(0, 3).map((c, i) => (
                    <a key={i} href={c.url} target="_blank" rel="noreferrer"
                        title={c.title}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--jr-info)]/[0.08] border border-[var(--jr-info)]/25 text-[var(--jr-info)] hover:bg-[var(--jr-info)]/[0.16] text-[10px] font-mono"
                        data-testid={`source-chip-${signal.id}-${i}`}>
                        <Link2 size={9} /> {hostOf(c.url)}
                        {c.published_date && <span className="text-[var(--jr-text-3)]">· {String(c.published_date).slice(0, 10)}</span>}
                    </a>
                ))}
                {urls.length > 3 && (
                    <span className="text-[10px] text-[var(--jr-text-3)]">+ {urls.length - 3}</span>
                )}
                {urls.length === 0 && meta.hint && (
                    <span className="text-[10px] text-[var(--jr-text-3)] italic">{meta.hint}</span>
                )}
            </div>
            {urls.length > 0 && (
                <button onClick={() => setOpen(!open)} type="button"
                    data-testid={`sources-toggle-${signal.id}`}
                    className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-white inline-flex items-center gap-1">
                    <BookOpen size={10} /> Bronnen ({urls.length}) {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                </button>
            )}
            {open && urls.length > 0 && (
                <ul className="mt-1 space-y-1 pl-3 border-l border-white/10">
                    {urls.map((c, i) => (
                        <li key={i} className="text-[11px] leading-snug">
                            <a href={c.url} target="_blank" rel="noreferrer"
                                className="text-[var(--jr-info)] hover:text-white hover:underline">
                                {c.title || c.url}
                            </a>
                            {c.published_date && (
                                <span className="ml-2 text-[10px] font-mono text-[var(--jr-text-3)]">{String(c.published_date).slice(0, 10)}</span>
                            )}
                            <div className="text-[10px] text-[var(--jr-text-3)] truncate">{hostOf(c.url)}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Signal card with contacts inline                                   */
/* ------------------------------------------------------------------ */
export function SignalCard({ signal: s, onPromote, onPromoteAndEnroll, promoting, onChanged, missions, selected, onToggleSelect, onArchive, onRestore, onDelete, onReject }) {
    const [expanded, setExpanded] = useState((s.contacts || []).length > 0);
    const [adding, setAdding] = useState(false);
    const contacts = s.contacts || [];
    const mission = s.hunt_mission_id ? missions.find((m) => m.id === s.hunt_mission_id) : null;
    const mtype = mission?.mission_type || "growth_signals";
    const tint = MISSION_TINT[mtype] || MISSION_TINT.growth_signals;
    const discoveredAt = fmtDateTime(s.discovered_at || s.created_at);
    const isArchived = s.status === "archived";

    const removeContact = async (contactId) => {
        try { await api.delete(`/signals/${s.id}/contacts/${contactId}`); toast.success(i18n.t("sigcard.contact_deleted")); onChanged?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
    };

    const eligible = s.status !== "qualified" && !isArchived;

    return (
        <div data-testid={`signal-${s.id}`}
            style={{ borderLeftColor: tint.color, borderLeftWidth: "4px" }}
            className={`rounded-xl border bg-[var(--jr-surface)] transition-colors ${isArchived ? "opacity-60 border-dashed border-white/10" : selected ? "border-[var(--jr-brand)] shadow-[0_0_0_2px_rgba(255,140,42,0.18)]" : (s.cronos_matches_count || 0) > 0 ? "border-[var(--jr-amber)]/50 shadow-[0_0_0_1px_rgba(255,140,42,0.14)]" : "border-white/8 hover:border-white/14"}`}>
            {/* Interne match banner — prioritair boven de card wanneer Cronos-pool een geschikte kandidaat heeft */}
            {(s.cronos_matches_count || 0) > 0 && s.top_cronos_match && (
                <Link to="/app/matches"
                    data-testid={`cronos-banner-${s.id}`}
                    className="flex items-center gap-2 px-5 py-2 border-b border-[var(--jr-amber)]/30 bg-gradient-to-r from-[var(--jr-amber)]/[0.12] to-transparent hover:from-[var(--jr-amber)]/[0.2] rounded-t-xl text-xs">
                    <Users size={13} className="text-[var(--jr-amber)] shrink-0"/>
                    <span className="text-white">
                        <span className="font-semibold">{i18n.t("sigcard.internal_match")}</span>{" "}
                        <span className="text-[var(--jr-amber)] font-medium">{s.top_cronos_match.name}</span>{" "}
                        · {s.top_cronos_match.confidence}% confidence
                        {s.cronos_matches_count > 1 && (
                            <span className="text-[var(--jr-text-3)]"> · +{s.cronos_matches_count - 1} {i18n.t("sigcard.other_candidates")}</span>
                        )}
                    </span>
                    <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-amber)]">
                        Cronos-groep first →
                    </span>
                </Link>
            )}
            <div className="p-5 flex items-start gap-4">
                {eligible && (
                    <button onClick={onToggleSelect} data-testid={`select-${s.id}`}
                        aria-label="Selecteer signaal"
                        className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${selected ? "bg-[var(--jr-brand)] border-[var(--jr-brand)]" : "border-white/20 hover:border-white/40"}`}>
                        {selected && <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </button>
                )}
                <div className="w-12 h-12 rounded-md bg-[var(--jr-brand)]/15 grid place-items-center shrink-0">
                    <Building2 className="text-[var(--jr-brand)]" size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-display text-lg tracking-tight">{s.company_name}</h3>
                        <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border ${TYPE_COLORS[s.signal_type] || "border-white/10 text-[var(--jr-text-2)]"}`}>{s.signal_type}</span>
                        <span title={`Mission-type: ${tint.label}`}
                            data-testid={`mtype-badge-${s.id}`}
                            style={{ background: `${tint.color}20`, color: tint.color, borderColor: `${tint.color}55` }}
                            className="text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-1 rounded border">
                            {tint.label}
                        </span>
                        <span className="text-[10px] font-mono text-[var(--jr-brand)] tracking-[0.16em]">Score {s.effective_score ?? s.score}</span>
                        {(s.conversion_boost || 0) > 0 && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[#22c55e]/12 text-[#22c55e] border border-[#22c55e]/30"
                                title={i18n.t("sigcard.conv_boost_tip")} data-testid={`conv-boost-${s.id}`}>
                                <Flame size={9} /> +{s.conversion_boost} {i18n.t("sigcard.conv_boost")}
                            </span>
                        )}
                        {/* Anti-hallucinatie trust-badge + verifieer-knop */}
                        <TrustBadge entity="signal" entityId={s.id} verification={s.verification}
                            onVerified={() => onChanged?.()} testid={`trust-signal-${s.id}`}/>
                        {/* Cross-source corroboratie: onafhankelijke 2e bron bevestigt dit signaal */}
                        {s.verification?.corroboration?.confirmed && (
                            <span title={`${i18n.t("sigcard.corroborated_tip")}: ${(s.verification.corroboration.independent_domains || []).join(", ")}`}
                                data-testid={`corroborated-badge-${s.id}`}
                                className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] inline-flex items-center gap-1">
                                <ShieldCheck size={10} /> {i18n.t("sigcard.corroborated", { count: (s.verification.corroboration.independent_domains || []).length })}
                            </span>
                        )}
                        {/* Twee-lagen model: 'vacature' = live gecheckt + Apply werkt;
                            'hiring_signal' = career-hint zonder Apply (geen jobkaart) */}
                        {s.layer === "vacature" && (
                            <span title={i18n.t("sigcard.layer_vacature_tip")}
                                data-testid={`layer-vacature-${s.id}`}
                                className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] inline-flex items-center gap-1">
                                <ShieldCheck size={10} /> {i18n.t("sigcard.layer_vacature")}
                            </span>
                        )}
                        {s.layer === "hiring_signal" && (
                            <span title={i18n.t("sigcard.layer_hiring_tip")}
                                data-testid={`layer-hiring-${s.id}`}
                                className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-[var(--jr-amber)]/40 bg-[var(--jr-amber)]/10 text-[var(--jr-amber)]">
                                {i18n.t("sigcard.layer_hiring")}
                            </span>
                        )}
                        {(s.open_vacancies_count || 0) > 0 && s.layer !== "hiring_signal" && (
                            <a href={s.careers_page_url || "#"} target={s.careers_page_url ? "_blank" : undefined} rel="noreferrer"
                                title={s.careers_page_url ? i18n.t("sigcard.view_careers") : i18n.t("sigcard.vacancies_detected")}
                                data-testid={`vacancy-count-${s.id}`}
                                onClick={(e) => !s.careers_page_url && e.preventDefault()}
                                className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] inline-flex items-center gap-1 hover:bg-[#22c55e]/20">
                                <Radar size={10}/> {s.open_vacancies_count} open jobs
                            </a>
                        )}
                        {s.status !== "new" && <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded bg-[var(--jr-success)]/15 text-[var(--jr-success)]">{s.status}</span>}
                        {mission && <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded bg-white/5 text-[var(--jr-text-2)]">via {mission.title}</span>}
                        {(s.source === "llm" || (s.source || "").endsWith("_llm_fallback")) && (
                            <span title={i18n.t("sigcard.synthetic_tip")}
                                className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-[var(--jr-error)]/40 bg-[var(--jr-error)]/10 text-[var(--jr-error)] inline-flex items-center gap-1"
                                data-testid={`synthetic-badge-${s.id}`}>
                                <AlertTriangle size={10} /> {i18n.t("sigcard.synthetic")}
                            </span>
                        )}
                        {s.needs_enrichment && (
                            <span title={i18n.t("sigcard.needs_enrich_tip")}
                                className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-[var(--jr-amber)]/40 bg-[var(--jr-amber)]/10 text-[var(--jr-amber)] inline-flex items-center gap-1"
                                data-testid={`needs-enrichment-${s.id}`}>
                                <AlertTriangle size={10} /> Needs enrichment
                            </span>
                        )}
                        {isArchived && (
                            <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-white/15 bg-white/[0.04] text-[var(--jr-text-2)] inline-flex items-center gap-1"
                                data-testid={`archived-badge-${s.id}`}>
                                <Archive size={10} /> {i18n.t("sigcard.archived")}
                            </span>
                        )}
                        {(s.communications_count || 0) > 0 && (
                            <Link to="/app/sales/outreach"
                                title={i18n.t("sigcard.comms_tip", { count: s.communications_count })}
                                data-testid={`comms-badge-${s.id}`}
                                className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-[var(--jr-info)]/40 bg-[var(--jr-info)]/10 text-[var(--jr-info)] inline-flex items-center gap-1 hover:bg-[var(--jr-info)]/20">
                                <Mail size={10} /> {s.communications_count} {i18n.t("sigcard.prev_comms")}
                            </Link>
                        )}
                        {(s.cronos_matches_count || 0) > 0 && (
                            <Link to="/app/matches"
                                title={i18n.t("sigcard.cronos_tip", { count: s.cronos_matches_count })}
                                data-testid={`cronos-match-${s.id}`}
                                className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border border-[var(--jr-amber)]/50 bg-[var(--jr-amber)]/15 text-[var(--jr-amber)] inline-flex items-center gap-1 hover:bg-[var(--jr-amber)]/25 font-semibold">
                                <Users size={10} /> {i18n.t("sigcard.internal_match_short")} {s.top_cronos_match?.name || s.cronos_matches_count} · {s.top_cronos_match?.confidence || ""}%
                            </Link>
                        )}
                    </div>
                    <div className="text-xs text-[var(--jr-text-2)] mt-1 flex items-center gap-3 flex-wrap">
                        <span>{s.industry || "—"} · {s.location || "—"} · {s.size || "—"}</span>
                        <span className="text-[var(--jr-text-3)] font-mono inline-flex items-center gap-1">
                            <Calendar size={10} /> {discoveredAt}
                        </span>
                    </div>
                    <div className="mt-3 font-medium text-white">{s.signal_title}</div>
                    <p className="text-sm text-[var(--jr-text-2)] mt-1 leading-relaxed">{s.signal_detail}</p>
                    <ApplyCta signal={s} />
                    {s.layer !== "hiring_signal" && <JobLinks signal={s} />}
                    <SourceChips signal={s} />
                </div>
                <div className="shrink-0 flex flex-col items-end gap-2">
                    {isArchived ? (
                        <button onClick={() => onRestore?.(s.id)} data-testid={`restore-${s.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[var(--jr-info)]/40 bg-[var(--jr-info)]/10 text-[var(--jr-info)] text-xs font-medium hover:bg-[var(--jr-info)]/20">
                            <ArchiveRestore size={12} /> Herstel
                        </button>
                    ) : s.status === "qualified" ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--jr-success)]/15 text-[var(--jr-success)] text-xs font-mono uppercase tracking-[0.16em]">Promoted</span>
                    ) : (
                        <div className="flex items-center gap-1.5">
                            {/* Thumbs-down (Reject with reason → archives + writes to agent_memory) */}
                            <button onClick={() => onReject?.(s)} disabled={promoting}
                                data-testid={`reject-${s.id}`}
                                title="Afkeuren — geef reden zodat de agent leert"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[var(--jr-error)]/40 bg-[var(--jr-error)]/10 text-[var(--jr-error)] text-xs font-medium hover:bg-[var(--jr-error)]/20 disabled:opacity-60">
                                <ThumbsDown size={12} /> Afkeuren
                            </button>
                            {/* Thumbs-up (Approve = Promote to Company). Split-button */}
                            <div className="flex items-center gap-1">
                                <button onClick={() => onPromote(s.id)} disabled={promoting} data-testid={`promote-${s.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-l-full bg-[#22c55e] text-black text-xs font-medium hover:bg-[#16a34a] disabled:opacity-60">
                                    {promoting ? <Loader2 className="animate-spin" size={12} /> : <ThumbsUp size={12} />} Goedkeuren
                                </button>
                                <PromoteCampaignPicker signalId={s.id} onSelect={onPromoteAndEnroll} disabled={promoting} testidPrefix={`atc-${s.id}`} />
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-1">
                        {!isArchived && (
                            <button onClick={() => onArchive?.(s.id)} data-testid={`archive-${s.id}`}
                                title={i18n.t("sigcard.archive_silent_tip")}
                                className="p-1.5 rounded-md border border-white/10 text-[var(--jr-text-3)] hover:text-white hover:bg-white/5">
                                <Archive size={13} />
                            </button>
                        )}
                        <button onClick={() => onDelete?.(s.id, s.company_name)} data-testid={`delete-${s.id}`}
                            title={i18n.t("sigcard.delete_tip")}
                            className="p-1.5 rounded-md border border-[var(--jr-error)]/25 text-[var(--jr-error)]/80 hover:text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10">
                            <Trash2 size={13} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Contacts section */}
            <div className="border-t border-white/8 px-5 py-3">
                <button onClick={() => setExpanded(!expanded)} data-testid={`contacts-toggle-${s.id}`}
                    className="w-full flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] hover:text-white">
                    <span className="flex items-center gap-2">
                        <Users size={11} />
                        {contacts.length} {contacts.length === 1 ? i18n.t("sigcard.contact_word") : i18n.t("sigcard.contacts_word")}
                        {contacts.length === 0 && <span className="text-[var(--jr-error)] normal-case tracking-normal">{i18n.t("sigcard.contact_required")}</span>}
                    </span>
                    {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
                {expanded && (
                    <div className="mt-3 space-y-2">
                        {contacts.map((c) => (
                            <ContactRow key={c.id} c={c} companyName={s.company_name} signalId={s.id}
                                onChanged={onChanged} onRemove={() => removeContact(c.id)} />
                        ))}
                        {!adding ? (
                            <button onClick={() => setAdding(true)} data-testid={`add-contact-${s.id}`}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-dashed border-white/15 text-[var(--jr-text-2)] hover:bg-white/5 hover:text-white">
                                <UserPlus size={11} /> Voeg contact toe
                            </button>
                        ) : (
                            <ContactCreateForm
                                signalId={s.id}
                                onCreated={() => { setAdding(false); onChanged?.(); }}
                                onCancel={() => setAdding(false)}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function ContactRow({ c, companyName, signalId, onChanged, onRemove }) {
    const [enriching, setEnriching] = useState(false);
    const [showDrawer, setShowDrawer] = useState(false);

    const enrichContact = async () => {
        setEnriching(true);
        try {
            const r = await api.post(`/hunt-missions/signals/${signalId}/contacts/${c.id}/enrich`);
            if (r.data?.ok) {
                toast.success(i18n.t("sigcard.enrich_done", { source: r.data.source || "LinkedIn" }));
                onChanged?.();
            } else {
                toast.info(r.data?.reason || i18n.t("sigcard.enrich_none"));
            }
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setEnriching(false); }
    };

    // Explain per-source what verification info is typically available so
    // the user understands the difference between Apollo (email + LinkedIn +
    // phone) and Surfe (LinkedIn URL, sometimes email — no phone by default).
    const sourceBadge = {
        apollo: { label: "✓ Apollo-verified", cls: "bg-[var(--jr-info)]/15 text-[var(--jr-info)] border-[var(--jr-info)]/30", tip: i18n.t("sigcard.tip_apollo") },
        surfe: { label: "✓ Surfe-verified", cls: "bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/30", tip: i18n.t("sigcard.tip_surfe") },
        theorg: { label: "✓ The Org", cls: "bg-[var(--jr-info)]/15 text-[var(--jr-info)] border-[var(--jr-info)]/30", tip: i18n.t("sigcard.tip_theorg") },
        manual: { label: i18n.t("sigcard.manual"), cls: "bg-white/[0.06] text-[var(--jr-text-2)] border-white/15", tip: i18n.t("sigcard.tip_manual") },
        linkedin: { label: "✓ LinkedIn", cls: "bg-[#0a66c2]/15 text-[#0a66c2] border-[#0a66c2]/30", tip: i18n.t("sigcard.tip_linkedin") },
        perplexity: { label: "✓ Perplexity", cls: "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/30", tip: i18n.t("sigcard.tip_pplx") },
    }[c.enrichment_source];
    return (
        <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)] p-3 flex items-start gap-3 flex-wrap" data-testid={`contact-${c.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-white truncate cursor-pointer hover:text-[var(--jr-brand)] hover:underline underline-offset-2 transition-colors"
                        onClick={() => setShowDrawer(true)} data-testid={`open-contact-${c.id}`}
                        title="Open contactfiche">{c.name}</span>
                    {c.is_primary && <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-brand)]/15 text-[var(--jr-brand)]">Primary</span>}
                    {c.network_degree && <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[#0a66c2]/15 text-[#0a66c2]">{c.network_degree} degree</span>}
                    {sourceBadge && (
                        <span title={sourceBadge.tip || `Geverifieerd via ${c.enrichment_source}`} data-testid={`enrichment-source-${c.id}`}
                            className={`text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border cursor-help ${sourceBadge.cls}`}>
                            {sourceBadge.label}
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-[var(--jr-text-3)] mt-0.5">
                    {c.role || "—"}
                    {companyName && (
                        <>
                            <span className="mx-1.5 text-[var(--jr-text-3)]/60">·</span>
                            <span className="inline-flex items-center gap-1 text-[var(--jr-text-2)]" data-testid={`contact-company-${c.id}`}>
                                <Building2 size={9} className="text-[var(--jr-brand)]/70" />
                                {companyName}
                            </span>
                        </>
                    )}
                </div>
                <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[11px]">
                    {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-[var(--jr-info)] hover:text-white"><Mail size={10} /> {c.email}</a>}
                    {c.phone && <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-[var(--jr-success)] hover:text-white"><Phone size={10} /> {c.phone}</a>}
                    <LinkedInButton url={c.linkedin_url} name={c.name} variant="inline" resolve={signalId ? { type: "signal_contact", id: c.id, signalId } : undefined} testid={`contact-linkedin-${c.id}`} />
                </div>
            </div>
            {(!c.email || !c.phone) && (
                <button onClick={enrichContact} disabled={enriching} data-testid={`enrich-contact-${c.id}`}
                    title={i18n.t("sigcard.enrich_tip")}
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-[var(--jr-brand)]/30 text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/10 disabled:opacity-50">
                    {enriching ? <Loader2 className="animate-spin" size={10} /> : <Sparkles size={10} />}
                    {i18n.t("sigcard.enrich_btn")}
                </button>
            )}
            <button onClick={onRemove} data-testid={`remove-contact-${c.id}`}
                className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10">
                <Trash2 size={12} />
            </button>
            {showDrawer && (
                <ContactDrawer contactRef={{ kind: "signal_contact", signalId, contactId: c.id }}
                    onClose={() => setShowDrawer(false)} onChanged={onChanged} />
            )}
        </div>
    );
}

function ContactCreateForm({ signalId, onCreated, onCancel }) {
    const [form, setForm] = useState({
        name: "", role: "", email: "", phone: "",
        linkedin_url: "", network_degree: "2nd",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error(i18n.t("sigcard.name_required")); return; }
        setSaving(true);
        try {
            await api.post(`/signals/${signalId}/contacts`, form);
            toast.success(i18n.t("sigcard.contact_added"));
            onCreated?.();
        } catch (err) { toast.error(err?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="rounded-lg border border-[var(--jr-brand)]/30 bg-[var(--jr-brand)]/5 p-3 grid grid-cols-1 md:grid-cols-12 gap-2"
            data-testid={`contact-form-${signalId}`}>
            <input required placeholder={i18n.t("search.lbl_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="md:col-span-3 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none" />
            <input placeholder={i18n.t("companies.lbl_role")} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="md:col-span-3 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none" />
            <input placeholder="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="md:col-span-3 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none" />
            <select value={form.network_degree} onChange={(e) => setForm({ ...form, network_degree: e.target.value })}
                className="md:col-span-3 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none">
                <option value="1st">1st degree</option>
                <option value="2nd">2nd degree</option>
                <option value="3rd">3rd degree</option>
                <option value="outside">Outside network</option>
            </select>
            <input placeholder="LinkedIn URL" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                className="md:col-span-6 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1.5 text-xs text-[var(--jr-text-2)] outline-none" />
            <input placeholder="GSM (+32 4XX...)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="md:col-span-3 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1.5 text-xs text-[var(--jr-text-2)] outline-none" />
            <div className="md:col-span-3 flex justify-end gap-1">
                <button type="button" onClick={onCancel} disabled={saving} className="text-xs px-2.5 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                <button type="submit" disabled={saving} data-testid={`contact-submit-${signalId}`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full bg-[var(--jr-brand)] text-black font-medium disabled:opacity-50">
                    {saving ? <Loader2 className="animate-spin" size={11} /> : <Plus size={11} />} {i18n.t("common.add")}
                </button>
            </div>
        </form>
    );
}
